import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, FileText, Shield, Sparkles, ExternalLink } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { COMPANY } from "@/lib/company";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { PoliceClearanceCopyEn } from "@/content/police-clearance/registry-en";

interface Facts {
  slug: string;
  flag: string;
  countryEn: string;
  certName: string;
  authorityShort: string;
  authorityFull: string;
  authorityUrl: string;
}

interface Related {
  slug: string;
  flag: string;
  countryEn: string;
  authorityShort: string;
  certName: string;
}

function buildFaqs(f: Facts, c: PoliceClearanceCopyEn) {
  return [
    {
      q: `Can I obtain a ${f.authorityShort} police certificate from Thailand?`,
      a: `Yes. Our Phuket team files the ${f.certName} on your behalf, so you do not need to travel to ${f.countryEn}. ${c.fingerprints}`,
    },
    { q: "How long does it take?", a: c.turnaround },
    { q: "How long is the certificate valid?", a: c.validity },
    { q: "Does it need an Apostille or MFA legalisation?", a: c.legalization },
    { q: "What can the certificate be used for?", a: `${c.useCases.join(" · ")}.` },
    {
      q: "How do I get a quote and start?",
      a: `Send photos of your passport and any request letter from the receiving authority to LINE ${COMPANY.lineId}, call ${COMPANY.phone} or email ${COMPANY.email}. Our staff confirm the document list, the service fee and the realistic timeline before any work begins.`,
    },
  ];
}

export const Route = createFileRoute("/en/police-clearance/$country")({
  loader: async ({ params }): Promise<{ facts: Facts; copy: PoliceClearanceCopyEn; related: Related[] }> => {
    const [{ getPoliceClearanceCountry, POLICE_CLEARANCE_COUNTRIES }, { getPoliceClearanceCopyEn, PC_COPY_EN }] =
      await Promise.all([
        import("@/content/police-clearance/registry"),
        import("@/content/police-clearance/registry-en"),
      ]);
    const c = getPoliceClearanceCountry(params.country);
    const copy = getPoliceClearanceCopyEn(params.country);
    if (!c || !copy) throw notFound();
    return {
      facts: {
        slug: c.slug,
        flag: c.flag,
        countryEn: c.countryEn,
        certName: c.certName,
        authorityShort: c.authorityShort,
        authorityFull: c.authorityFull,
        authorityUrl: c.authorityUrl,
      },
      copy,
      related: POLICE_CLEARANCE_COUNTRIES.filter((x) => x.slug !== c.slug && PC_COPY_EN[x.slug])
        .slice(0, 6)
        .map((x) => ({
          slug: x.slug,
          flag: x.flag,
          countryEn: x.countryEn,
          authorityShort: x.authorityShort,
          certName: x.certName,
        })),
    };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Police certificate not found" }, { name: "robots", content: "noindex" }],
      };
    }
    const { facts, copy } = loaderData;
    const site = getActiveSiteUrl();
    const title = `${facts.authorityShort} Police Certificate from Thailand`;
    const base = pageHeadForProvince({
      title,
      description: `Apply for the ${facts.certName} from Phuket without returning to ${facts.countryEn} — fingerprinting, filing, Apostille, certified translation and Thai MFA legalisation handled by our team.`,
      path: `/en/police-clearance/${facts.slug}`,
      type: "article",
      locale: "en_US",
      alternates: [{ lang: "th", path: `/police-clearance/${params.country}` }],
      keywords: [
        `${facts.authorityShort} police check thailand`,
        `${facts.certName}`,
        `police clearance ${facts.countryEn.toLowerCase()} from thailand`,
        "police clearance certificate phuket",
      ],
    });
    const faqs = buildFaqs(facts, copy);
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Police Clearance", url: `${site}/en/police-clearance` },
              { name: facts.countryEn, url: `${site}/en/police-clearance/${facts.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/en/police-clearance/${facts.slug}#service`,
            name: `${facts.certName} from Phuket`,
            serviceType: "Police Clearance Certificate Procurement",
            description: copy.aeoAnswer,
            provider: {
              "@type": "LegalService",
              name: COMPANY.legalName,
              telephone: COMPANY.phoneIntl,
              url: site,
            },
            areaServed: { "@type": "Country", name: facts.countryEn },
            isRelatedTo: {
              "@type": "GovernmentOrganization",
              name: facts.authorityFull,
              url: facts.authorityUrl,
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: `How to obtain a ${facts.certName} from Thailand`,
            description: copy.intro,
            step: copy.steps.map((s: { title: string; body: string }, i: number) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.title,
              text: s.body,
            })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/police-clearance/${facts.slug}`)) },
      ],
    };
  },
  component: PoliceClearanceCountryPageEn,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">Country not covered yet</h1>
        <Link to="/en/police-clearance" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> Back to all countries
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
});

function PoliceClearanceCountryPageEn() {
  const { facts, copy, related } = Route.useLoaderData();
  const faqs = buildFaqs(facts, copy);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav aria-label="breadcrumb" className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <a href="/en" className="hover:text-primary">Home</a>
            <span className="mx-2">/</span>
            <a href="/en/police-clearance" className="hover:text-primary">Police Clearance</a>
            <span className="mx-2">/</span>
            <span className="text-foreground">{facts.countryEn}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              <Shield className="mr-1 inline h-3.5 w-3.5" />
              {facts.flag} {facts.authorityShort} · {facts.countryEn}
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">{facts.certName} from Phuket</h1>
            <p id="ai-answer-tldr" data-speakable="true" className="speakable-summary mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">
                <Sparkles className="mr-1 inline h-3.5 w-3.5" /> AI Quick Answer
              </span>
              {copy.aeoAnswer}
            </p>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">{copy.intro}</p>
            <div className="mt-5 rounded-lg border bg-muted/30 p-3 text-sm">
              <strong>ภาษาไทย:</strong>{" "}
              <a href={`/police-clearance/${facts.slug}`} className="text-primary underline">
                อ่านหน้าภาษาไทย →
              </a>
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">Turnaround</div>
              <div className="mt-1 text-sm font-bold">{copy.turnaround}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">Validity</div>
              <div className="mt-1 text-sm font-bold">{copy.validity}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">Issuing authority</div>
              <div className="mt-1 text-sm font-bold">{facts.authorityShort}</div>
              <a
                href={facts.authorityUrl}
                target="_blank"
                rel="noopener nofollow"
                className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-gold"
              >
                Official website <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">What it is used for</h2>
            <ul className="mt-5 grid gap-3 md:grid-cols-2">
              {copy.useCases.map((u: string, i: number) => (
                <li key={i} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">Step by step from Phuket</h2>
            <ol className="mt-5 space-y-3">
              {copy.steps.map((s: { title: string; body: string }, i: number) => (
                <li key={i} className="flex gap-3 rounded-xl border border-border bg-card p-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold text-sm font-bold text-foreground">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-bold text-primary">{s.title}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{s.body}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto grid max-w-5xl gap-4 px-4 py-12 md:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-sm font-bold text-primary">
                <FileText className="h-4 w-4 text-gold" /> Fingerprints
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{copy.fingerprints}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-sm font-bold text-primary">
                <Shield className="h-4 w-4 text-gold" /> Apostille / legalisation
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{copy.legalization}</p>
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-3xl px-4 py-12">
            <h2 className="text-2xl font-bold">Frequently asked questions</h2>
            <div className="mt-5 space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                  <summary className="cursor-pointer font-semibold text-primary">{f.q}</summary>
                  <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-xl font-bold">Other countries we cover</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {related.map((r: Related) => (
                <a
                  key={r.slug}
                  href={`/en/police-clearance/${r.slug}`}
                  className="rounded-xl border border-border bg-card p-4 text-sm transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <span className="text-xl">{r.flag}</span>
                  <div className="mt-1 font-bold text-primary">{r.authorityShort}</div>
                  <div className="text-xs text-muted-foreground">{r.countryEn}</div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 py-12">
          <h2 className="text-xl font-bold">Talk to our team</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Service fees depend on the authority fee, legalisation route and courier, so we quote per case. Contact us by
            phone {COMPANY.phone}, LINE {COMPANY.lineId} or email {COMPANY.email} — Mon-Sat 9:00-18:00.
          </p>
          <a href="/contact" className="mt-4 inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">
            Contact the team →
          </a>
          <ul className="mt-6 grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/en/consular-legalization/faq">MFA legalisation &amp; Apostille FAQ</a></li>
            <li><a className="text-primary underline" href="/en/translation/faq">Certified translation FAQ</a></li>
            <li><a className="text-primary underline" href="/en/notary/faq">Notarial services &amp; POA FAQ</a></li>
            <li><a className="text-primary underline" href="/en/visa/faq">Visa &amp; immigration FAQ</a></li>
          </ul>
          <FaqSourceNote topic="police-clearance" locale="en" />
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
